import React, { useState } from 'react';
import { Save, Briefcase } from 'lucide-react';
import { Button } from '../../../../components/ui/button';
import { Input } from '../../../../components/ui/input';
import { Textarea } from '../../../../components/ui/textarea';
import { Label } from '../../../../components/ui/label';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '../../../../components/ui/card';
import FormFieldItem from '../../../../components/common/FormFieldItem';
import SourceCombobox from '../../../../components/common/SourceCombobox';
import {
  PAMM_CURRENCY_OPTIONS,
  PAMM_RISK_LEVEL_OPTIONS,
  PAMM_LEVERAGE_OPTIONS,
  PAMM_ALLOCATION_METHOD_OPTIONS,
  PAMM_TRADING_SYMBOLS,
  PAMM_FUND_STATUS_OPTIONS,
} from '../../constants';
import type { FundStatus, AllocationMethod, RiskLevel } from '../../types';
import toast from 'react-hot-toast';
import {
  preventInvalidNumberInput,
  preventInvalidNumberPaste,
  preventInvalidNameInput,
  validateFundForm,
  FundFormData,
} from '../../../../utils/validationMessages';

export interface PAMMFundFormValues {
  name: string;
  description: string;
  currency: string;
  minimumInvestment: string | number;
  maximumInvestment: string | number;
  performanceFee: string | number;
  managementFee: string | number;
  entryFee: string | number;
  exitFee: string | number;
  riskLevel: RiskLevel;
  leverage: string;
  allocationMethod: AllocationMethod;
  lockPeriodDays: string | number;
  status?: FundStatus;
}

const DEFAULT_INITIAL_FORM: PAMMFundFormValues = {
  name: '',
  description: '',
  currency: 'USD',
  minimumInvestment: '',
  maximumInvestment: '',
  performanceFee: '',
  managementFee: '',
  entryFee: '0',
  exitFee: '1',
  riskLevel: 'medium',
  leverage: '1:100',
  allocationMethod: 'percentage',
  lockPeriodDays: '30',
  status: 'active',
};

export interface PAMMFundFormProps {
  mode: 'create' | 'edit';
  initialValues?: Partial<PAMMFundFormValues>;
  initialSymbols?: string[];
  submitting?: boolean;
  onSubmit: (
    formValues: PAMMFundFormValues,
    selectedSymbols: string[]
  ) => Promise<void>;
  onCancel: () => void;
}

export const PAMMFundForm: React.FC<PAMMFundFormProps> = ({
  mode,
  initialValues,
  initialSymbols = [],
  submitting = false,
  onSubmit,
  onCancel,
}) => {
  const [form, setForm] = useState<PAMMFundFormValues>(() => ({
    ...DEFAULT_INITIAL_FORM,
    ...initialValues,
  }));
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [selectedSymbols, setSelectedSymbols] = useState<string[]>(() =>
    initialSymbols && initialSymbols.length > 0 ? initialSymbols : []
  );

  const set = (field: keyof PAMMFundFormValues, value: any) => {
    setForm(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => {
        const next = { ...prev };
        delete next[field];
        return next;
      });
    }
  };

  const toggleSymbol = (sym: string) => {
    setSelectedSymbols(prev =>
      prev.includes(sym) ? prev.filter(s => s !== sym) : [...prev, sym]
    );
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const result = validateFundForm(form as unknown as FundFormData);
    if (!result.valid) {
      setErrors(result.errors);
      toast.error('Please fix the validation errors.');
      return;
    }
    await onSubmit(form, selectedSymbols);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-5">
      {/* Basic Info */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-semibold flex items-center gap-2">
            <Briefcase className="h-4 w-4" />
            Basic Information
          </CardTitle>
        </CardHeader>
        <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-5">
          <div className="md:col-span-2">
            <FormFieldItem label="Fund Name" error={errors.name} required>
              <Input
                placeholder="e.g., Alpha Growth Fund"
                value={form.name}
                onKeyDown={preventInvalidNameInput}
                onChange={e => set('name', e.target.value)}
                className={errors.name ? 'border-red-400' : ''}
              />
            </FormFieldItem>
          </div>
          <div className="md:col-span-2">
            <FormFieldItem
              label="Description"
              error={errors.description}
              required
            >
              <Textarea
                placeholder="Describe the fund strategy, objectives, and risk approach..."
                value={form.description}
                onChange={e => set('description', e.target.value)}
                rows={3}
                className={errors.description ? 'border-red-400' : ''}
              />
            </FormFieldItem>
          </div>
          <FormFieldItem label="Currency" error={errors.currency} required>
            <SourceCombobox
              items={PAMM_CURRENCY_OPTIONS}
              value={form.currency}
              onChange={v => set('currency', v)}
              placeholder="Select Currency"
            />
          </FormFieldItem>
          <FormFieldItem label="Risk Level" error={errors.riskLevel} required>
            <SourceCombobox
              items={PAMM_RISK_LEVEL_OPTIONS}
              value={form.riskLevel}
              onChange={v => set('riskLevel', v)}
              placeholder="Select Risk Level"
            />
          </FormFieldItem>
          {mode === 'edit' && (
            <FormFieldItem label="Status">
              <SourceCombobox
                items={PAMM_FUND_STATUS_OPTIONS}
                value={form.status}
                onChange={v => set('status', v)}
                placeholder="Select Status"
              />
            </FormFieldItem>
          )}
        </CardContent>
      </Card>

      {/* Investment Limits */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-semibold">
            Investment Limits
          </CardTitle>
        </CardHeader>
        <CardContent className="grid grid-cols-1 sm:grid-cols-2 gap-5">
          <FormFieldItem
            label="Minimum Investment ($)"
            error={errors.minimumInvestment}
            required
          >
            <Input
              type="number"
              min="100"
              placeholder="e.g., 5000"
              value={form.minimumInvestment}
              onKeyDown={preventInvalidNumberInput}
              onPaste={preventInvalidNumberPaste}
              onChange={e => set('minimumInvestment', e.target.value)}
              className={errors.minimumInvestment ? 'border-red-400' : ''}
            />
          </FormFieldItem>
          <FormFieldItem
            label="Maximum Investment ($)"
            error={errors.maximumInvestment}
            required
          >
            <Input
              type="number"
              min="100"
              placeholder="e.g., 500000"
              value={form.maximumInvestment}
              onKeyDown={preventInvalidNumberInput}
              onPaste={preventInvalidNumberPaste}
              onChange={e => set('maximumInvestment', e.target.value)}
              className={errors.maximumInvestment ? 'border-red-400' : ''}
            />
          </FormFieldItem>
          <FormFieldItem
            label="Lock Period (Days)"
            error={errors.lockPeriodDays}
            required
          >
            <Input
              type="number"
              min="0"
              placeholder="e.g., 30"
              value={form.lockPeriodDays}
              onKeyDown={preventInvalidNumberInput}
              onPaste={preventInvalidNumberPaste}
              onChange={e => set('lockPeriodDays', e.target.value)}
              className={errors.lockPeriodDays ? 'border-red-400' : ''}
            />
          </FormFieldItem>
          <FormFieldItem
            label="Allocation Method"
            error={errors.allocationMethod}
            required
          >
            <SourceCombobox
              items={PAMM_ALLOCATION_METHOD_OPTIONS}
              value={form.allocationMethod}
              onChange={v => set('allocationMethod', v)}
              placeholder="Select Allocation Method"
            />
          </FormFieldItem>
        </CardContent>
      </Card>

      {/* Fees */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-semibold">Fee Structure</CardTitle>
        </CardHeader>
        <CardContent className="grid grid-cols-2 sm:grid-cols-4 gap-5">
          <FormFieldItem
            label="Performance Fee (%)"
            error={errors.performanceFee}
            required
          >
            <Input
              type="number"
              min="0"
              max="50"
              step="0.5"
              placeholder="e.g., 20"
              value={form.performanceFee}
              onKeyDown={preventInvalidNumberInput}
              onPaste={preventInvalidNumberPaste}
              onChange={e => set('performanceFee', e.target.value)}
              className={errors.performanceFee ? 'border-red-400' : ''}
            />
          </FormFieldItem>
          <FormFieldItem
            label="Management Fee (%/yr)"
            error={errors.managementFee}
            required
          >
            <Input
              type="number"
              min="0"
              max="10"
              step="0.1"
              placeholder="e.g., 2"
              value={form.managementFee}
              onKeyDown={preventInvalidNumberInput}
              onPaste={preventInvalidNumberPaste}
              onChange={e => set('managementFee', e.target.value)}
              className={errors.managementFee ? 'border-red-400' : ''}
            />
          </FormFieldItem>
          <FormFieldItem label="Entry Fee (%)">
            <Input
              type="number"
              min="0"
              max="10"
              step="0.1"
              placeholder="e.g., 0"
              value={form.entryFee}
              onKeyDown={preventInvalidNumberInput}
              onPaste={preventInvalidNumberPaste}
              onChange={e => set('entryFee', e.target.value)}
            />
          </FormFieldItem>
          <FormFieldItem label="Exit Fee (%)">
            <Input
              type="number"
              min="0"
              max="10"
              step="0.1"
              placeholder="e.g., 1"
              value={form.exitFee}
              onKeyDown={preventInvalidNumberInput}
              onPaste={preventInvalidNumberPaste}
              onChange={e => set('exitFee', e.target.value)}
            />
          </FormFieldItem>
        </CardContent>
      </Card>

      {/* Trading */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-semibold">
            Trading Settings
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-5">
          <FormFieldItem label="Leverage" error={errors.leverage} required>
            <div className="w-48">
              <SourceCombobox
                items={PAMM_LEVERAGE_OPTIONS}
                value={form.leverage}
                onChange={v => set('leverage', v)}
                placeholder="Select Leverage"
              />
            </div>
          </FormFieldItem>
          <div>
            <Label className="text-xs font-medium mb-2 block text-foreground">
              Trading Symbols
            </Label>
            <div className="flex flex-wrap gap-2">
              {PAMM_TRADING_SYMBOLS.map(sym => (
                <button
                  key={sym}
                  type="button"
                  onClick={() => toggleSymbol(sym)}
                  className={`px-2.5 py-1 text-xs rounded-full border transition-colors font-medium ${
                    selectedSymbols.includes(sym)
                      ? 'bg-primary text-primary-foreground border-primary'
                      : 'bg-background text-foreground border-border hover:border-primary/50'
                  }`}
                >
                  {sym}
                </button>
              ))}
            </div>
            {selectedSymbols.length > 0 && (
              <p className="text-xs text-muted-foreground mt-2">
                {selectedSymbols.length} symbol(s) selected
              </p>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Submit / Cancel Buttons */}
      <div className="flex items-center justify-end gap-3 pt-2">
        <Button type="button" variant="outline" onClick={onCancel}>
          Cancel
        </Button>
        <Button type="submit" disabled={submitting}>
          <Save className="h-3.5 w-3.5 mr-1.5" />
          {submitting
            ? mode === 'create'
              ? 'Creating...'
              : 'Saving...'
            : mode === 'create'
              ? 'Save'
              : 'Save Changes'}
        </Button>
      </div>
    </form>
  );
};

export default PAMMFundForm;
